import {
  Field,
  Form,
  Formik,
  FormikConfig,
  FormikProps,
  FormikValues,
} from 'formik';
import * as React from 'react';

import clsx from 'clsx';
import { DialogActions, DialogContent, Stack } from '@mui/material';

export type DynamicField<V = any> = {
  name: string;
  label?: string;
  component: React.ComponentType<V>;
};

type Props<Values extends FormikValues = FormikValues> = {
  initialValues: Values;
  fields: DynamicField[];
  fieldRender?: (
    form: FormikProps<Values>,
    field: any,
    index: number,
  ) => JSX.Element | null;
  submit: React.ReactElement<any, any>;
  bottomFields?: DynamicField[];
  cancelButton: React.ReactElement<any, any>;
} & FormikConfig<Values>;

function DynamicFormWithDialog<Values extends FormikValues>(
  {
    initialValues,
    fields,
    onSubmit,
    fieldRender,
    submit,
    bottomFields,
    cancelButton,
    ...props
  }: Props<Values>,
  ref: React.ForwardedRef<FormikProps<Values>>,
) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={ref}
      {...props}
    >
      {form => {
        const submitButton = React.cloneElement(submit, {
          onClick: () => form.submitForm(),
          disabled:
            submit?.props?.disabled || !form.isValid || form.isSubmitting,
          className: clsx(submit.props.className, {
            disabled: !form.isValid || form.isSubmitting,
          }),
        });

        return (
          <Form
            // This style to fix issue => (wrapping dialogActions with formik) to make buttons fixed instead scroll
            style={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
            className="document-upload"
          >
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', border: 'none' }}
            >
              {fields.map((field, index) => {
                if (typeof fieldRender === 'function') {
                  return fieldRender(form, field, index);
                }
                return <Field key={field.name} {...field} />;
              })}
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: 'flex-end', paddingBottom: '24px' }}
            >
              <Stack direction="row" spacing={4}>
                {cancelButton}
                {submitButton}
              </Stack>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}

export default React.forwardRef(DynamicFormWithDialog) as <
  Values extends FormikValues
>(
  props: Props<Values> & { ref?: React.ForwardedRef<FormikProps<Values>> },
) => ReturnType<typeof DynamicFormWithDialog>;
